import { useState, useContext, useEffect, useCallback } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import StatCards from "./components/StatCards";
import MobileStatCards from "./components/MobileStatCards";
import ReBox from "../../common/ReBox";
import PageHeader from "../../common/PageHeader";
import * as states from "./__fixtures__/initialStates";
import InfoIcon from "@mui/icons-material/Info";
import ScoreGauge from "./components/ScoreGauge";
import DropdownRadioList from "../../common/DropdownRadioList";
import DateRange from "../../common/DateRange";
import { GlobalContext, FunctionsContext } from "../../common/Context";
import LoadingWheel from "../../common/LoadingWheel";
import { endOfISOWeek, startOfISOWeek } from "date-fns";
import StatContainer from "./components/StatContainer";
import StatDisplay from "./components/StatDisplay";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { purple, grey, blue, red, border } from "../../../theme/colours";
import { STATS_GAUGE } from "../../../constants/ids";

const Stats = () => {
  const currentWeekDate = moment();
  const lastWeekDate = moment().subtract(1, "weeks");

  const { t } = useTranslation(["Stats", "ScoreCategories"]);
  const {
    userState,
    mobileView,
    tabletView,
    desktopView,
    firebaseState,
    companyState,
  } = useContext(GlobalContext);
  const { getAverages, getTierGroupsByCompany } = useContext(FunctionsContext);
  const averages = getAverages(userState?.profileID);

  const companyGroups = getTierGroupsByCompany(
    userState.isAdmin || userState.isMedidas ? companyState?.companyID : null,
  );

  const [tierGroups, setTierGroups] = useState();
  const [allDataTW, setAllDataTW] = useState([]);
  const [allDataLW, setAllDataLW] = useState([]);
  const [scoresTW, setScoresTW] = useState(states.initialScores);
  const [scoresLW, setScoresLW] = useState(states.initialScores);
  const [personalScores, setPersonalScore] = useState(states.initialScores);
  const [sortedScores, setSortedScores] = useState(states.initialImproved);
  const [mostImproved, setMostImproved] = useState(states.initialImproved);
  const [flippyLoading, setFlippyLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [flippyValues, setFlippyValues] = useState(states.initFlippys);
  const [selectedTeam, setSelectedTeam] = useState({
    name: t("personal"),
    id: "Personal",
  });
  const [teams, setTeams] = useState([]);
  const [dateRange, setDateRange] = useState([
    {
      startDate: startOfISOWeek(currentWeekDate.toDate()),
      endDate: endOfISOWeek(currentWeekDate.toDate()),
      key: "selection",
    },
  ]);
  const [userDateRange, setUserDateRange] = useState({
    min: currentWeekDate.format("YYYY-WW"),
    max: currentWeekDate.format("YYYY-WW"),
  });
  const [lastWeek, setLastWeek] = useState({
    min: lastWeekDate.format("YYYY-WW"),
    max: lastWeekDate.format("YYYY-WW"),
  });
  const [rangeType, setRangeType] = useState("week");
  const [totalComp, setTotalComp] = useState("");
  const [teamTW, setTeamTW] = useState([]);
  const [userTW, setUserTW] = useState();
  const [teamLW, setTeamLW] = useState([]);
  const [userLW, setUserLW] = useState();

  const userLoading = !userState?.profileID;
  const statsWheels = userLoading || statsLoading;

  const contStyle = {
    width: "100%",
    mt: mobileView ? "3%" : "1rem",
    ...(mobileView && {
      "& h2": {
        fontSize: "1rem",
        mb: "3%",
      },
    }),
  };
  const statsTitleStyle = {
    width: tabletView ? "50%" : "100%",
  };
  const userStatsArea = {
    display: "flex",
    flexDirection: "column",
    mb: "1rem",
  };
  const headerArea = {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  };
  const ddlLoadBox = {
    width: mobileView ? "100%" : "30%",
    height: "3.5rem",
    mb: "2rem",
  };
  const titleStyle = {
    mb: desktopView ? "1rem" : "0.5rem",
    ...(!desktopView && {
      fontSize: "1rem",
    }),
  };
  const flippyArea = {
    position: "relative",
    display: !mobileView && !tabletView && "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
  };
  const noDataBox = {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
    zIndex: 1,
  };
  const overlay = {
    position: "absolute",
    width: "100%",
    height: "100%",
    background: "#f4f2f8",
    opacity: 0.5,
  };
  const noDataMessage = {
    color: "#2e117f",
    top: mobileView ? "-30%" : tabletView ? "-25%" : 0,
  };
  const lineBox = {
    width: "100%",
    height: "2px",
    backgroundColor: "#EAEAEA",
    m: mobileView || tabletView ? "1rem 0" : "0 0 2rem 0",
  };
  const subtitleAreaStyle = {
    display: "flex",
    flexDirection: !desktopView ? "column" : "row",
    justifyContent: "space-between",
    mb: mobileView ? "10%" : "3rem",
  };
  const subtitleStyle = {
    width: !desktopView ? "90%" : "60%",
    fontSize: "0.875rem",
  };
  const dateRangeStyle = {
    height: "3.5rem",
    width: mobileView ? "100%" : tabletView ? "50%" : "30%",
    mt: tabletView ? "2%" : mobileView && "10%",
  };
  const trackStats = {
    position: "relative",
    display: "flex",
    flexDirection: mobileView || tabletView ? "column" : "row",
    minHeight: !mobileView && "31rem",
  };
  const gaugeBoxArea = {
    width: mobileView || tabletView ? "100%" : "580px",
    mb: tabletView && "4rem",
  };
  const overallHeaderStyle = {
    pl: "15px",
    borderRadius: "10px 10px 0 0",
    backgroundColor: purple[4],
    boxShadow: "0px 2px 3px 1px #dad8e8",
    textTransform: "uppercase",
    fontWeight: 800,
    lineHeight: "2.5rem",
  };
  const gaugeBoxStyle = {
    display: "flex",
    flexDirection: mobileView ? "column" : "row",
    justifyContent: "space-between",
    height: tabletView ? "100%" : "92%",
    padding: mobileView ? "20px" : "30px",
    borderRadius: "0 0 10px 10px",
    mb: mobileView ? "1rem" : "0",
  };
  const largeGaugeAreaStyle = {
    width: tabletView ? "33%" : "300px",
    mr: !tabletView && "20px",
  };
  const largeGaugeBoxStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: mobileView ? "185px" : "90%",
    padding: mobileView ? "0 25px" : "25px",
    boxShadow: "inset 0 0 8px #cdcdcd",
    borderRadius: "5px",
  };
  const largeGaugeText = {
    display: "flex",
    alignItems: "center",
    height: "10%",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "1.2",
    marginTop: mobileView && "0.5rem",
    pt: !mobileView && "30px",
  };
  const groupGaugeArea = {
    width: tabletView ? "64%" : "180px",
    ml: !tabletView && "20px",
  };
  const groupGaugeMobile = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
  const groupGaugeBoxStyle = {
    display: "flex",
    flexDirection: !desktopView ? "row" : "column",
    justifyContent: "space-between",
    alignItems: "center",
    height: "90%",
    margin: mobileView && "2rem 0 0.5rem",
    padding: desktopView && "10px",
    boxShadow: desktopView && "inset 0 0 8px #cdcdcd",
    borderRadius: "5px",
  };
  const infoAreaStyle = {
    display: "flex",
    justifyContent: desktopView && "space-around",
    alignItems: "center",
    height: "10%",
    pt: desktopView ? "1.875rem" : tabletView && "0.875rem",
  };
  const skillScoreStyle = {
    fontWeight: 800,
    fontSize: "0.875rem",
  };
  const infoIconStyle = {
    marginLeft: !desktopView && "1rem",
    color: grey[1],
    fontSize: "0.875rem",
  };
  const statBox = {
    display: "flex",
    flexDirection: !tabletView ? "column" : "row",
    justifyContent: "space-between",
    flexGrow: "1",
    ml: mobileView || tabletView ? "0" : "3%",
    width: "inherit",
    pb: tabletView && "2rem",
  };
  const baseRowBox = {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: !desktopView ? "column" : "row",
  };
  const bestRowBox = {
    ...baseRowBox,
    flex: desktopView ? 0.875 : 1,
  };
  const worstRowBox = {
    ...baseRowBox,
    flex: desktopView ? 1.125 : 1,
  };
  const vLine = {
    height: "100%",
    border: "solid 1px",
    borderColor: "#eaeaea",
  };
  const hLine = {
    width: tabletView ? "2%" : "100%",
    ...(desktopView && {
      border: `solid 1px ${grey[1]}`,
      m: "1.5rem 0",
    }),
    ...(mobileView && {
      borderTop: `1px dashed ${border.primary}`,
      m: "0.75rem 0 1.5rem",
    }),
  };
  const progTextArea = {
    ...(desktopView && {
      width: "65%",
    }),
  };

  const setFormattedRange = (range) => {
    const rangeStart = moment(range[0].startDate);
    const rangeEnd = moment(range[0].endDate);
    setDateRange(range);
    setUserDateRange({
      min: rangeStart.format("YYYY-WW"),
      max: rangeEnd.format("YYYY-WW"),
    });
    setLastWeek({
      min: rangeStart.subtract(1, "weeks").format("YYYY-WW"),
      max: rangeEnd.subtract(1, "weeks").format("YYYY-WW"),
    });
  };

  const compareWeek = (statName) => {
    const thisWeek = scoresTW[statName];
    const lastWeek = scoresLW[statName];
    if (thisWeek > lastWeek)
      return t("statUpBy", { amount: thisWeek - lastWeek });
    else if (thisWeek < lastWeek)
      return t("statDownBy", { amount: lastWeek - thisWeek });
    return t("statNoChange");
  };

  const compareTotal = useCallback(
    (tTotal, lTotal) => {
      const who = isNaN(selectedTeam.id) ? t("yourTotal") : t("yourTeamTotal");
      const diff = tTotal - lTotal;
      setTotalComp(
        diff < 0
          ? t("compareTotal", {
              context: "down",
              who,
              diff: Math.abs(diff),
              range: t(rangeType),
            })
          : diff > 0
          ? t("compareTotal", { context: "up", who, diff, range: t(rangeType) })
          : t("compareTotal", { context: "same", who, range: t(rangeType) }),
      );
    },
    [rangeType, selectedTeam, t],
  );

  const convertScores = (scores) => ({
    acceleration: scores.acceleration,
    focus: scores.alert,
    handling: scores.cool,
    cornering: scores.cornering,
    braking: scores.deceleration,
    eco: scores.eco,
    steadiness: scores.jerk,
    phone: scores.phoneHandling,
    speed: scores.speeding,
    swerve: scores.swerve,
    total: scores.total,
  });

  const buildTeamArray = useCallback((teams, personal) => {
    const newTeams = [
      ...teams,
      {
        id: "Personal",
        groupName: "Personal",
        scores: Object.keys(personal).length ? personal : states.tlStats,
      },
    ];
    const teamsFormatted = newTeams.map((team) => ({
      ...team,
      scores: convertScores(team.scores),
    }));
    return teamsFormatted;
  }, []);

  const mapTeams = useCallback((teamList) => {
    let mappedTeams = [];
    for (const team of teamList) {
      if (!team.subteams) {
        mappedTeams.push({
          id: team.id,
          name: team.name,
        });
      } else {
        mappedTeams = [
          ...mappedTeams,
          { id: team.id, name: team.name },
          ...mapTeams(team.subteams),
        ];
      }
    }
    return mappedTeams;
  }, []);

  useEffect(() => {
    if (
      companyGroups.data &&
      userState.teams &&
      (userState.isAdmin || userState.isMedidas)
    ) {
      let uniqueGroups = companyGroups.data.map((group) => ({
        name: group.name,
        id: group.id,
      }));
      for (const group of userState.teams) {
        if (
          !companyGroups.data.find((inner) => {
            inner.id === group.id;
          })
        ) {
          uniqueGroups = [...uniqueGroups, { name: group.name, id: group.id }];
        }
      }
      setTierGroups(uniqueGroups);
    }
    if (userState.teams && !(userState.isAdmin || userState.isMedidas)) {
      let uniqueGroups = userState.teams.map((group) => ({
        name: group.name,
        id: group.id,
      }));
      setTierGroups(uniqueGroups);
    }
  }, [companyGroups, userState.teams]);

  useEffect(() => {
    if (tierGroups) {
      if (tierGroups.error) {
        console.error(tierGroups.error);
        return;
      }
      if (tierGroups.isLoading) {
        return;
      }
      const teams = tierGroups.map((group) => ({
        name: group.name,
        id: group.id,
      }));
      setTeams(mapTeams(teams || []));
    }
  }, [mapTeams, tierGroups]);

  useEffect(() => {
    !userState.profileID &&
      teams.length &&
      setSelectedTeam({
        name: teams[0].name,
        id: teams[0].id.toString(),
      });
  }, [teams, userState.profileID]);

  useEffect(() => {
    if (!userState.teams?.length) return;

    setTeams(userState.teams);
  }, [userState.teams]);

  useEffect(() => {
    if (averages.error) {
      console.error(averages.error);
      return;
    }
    if (averages.isLoading) {
      setFlippyLoading(true);
      return;
    }

    setFlippyValues((prevValues) => ({
      ...prevValues,
      avgEvents: averages.data.events,
      avgDriverScore: averages.data.total,
      totalTryps: averages.data.totalTryps,
    }));
    setFlippyLoading(false);
  }, [averages]);

  useEffect(() => {
    const getUserTW = async () => {
      setStatsLoading(true);
      setUserTW(
        await firebaseState.getUserScoresInRange(
          userDateRange.min,
          userDateRange.max,
          userState.profileID,
        ),
      );
    };
    try {
      userState.profileID && getUserTW();
    } catch (e) {
      console.error(e.message);
    }
  }, [
    firebaseState,
    userState.profileID,
    userDateRange.min,
    userDateRange.max,
  ]);

  useEffect(() => {
    const getUserLW = async () => {
      setStatsLoading(true);
      const userLW = await firebaseState.getUserScoresInRange(
        lastWeek.min,
        lastWeek.max,
        userState.profileID,
      );
      setUserLW(userLW);
      setFlippyValues((prevValues) =>
        prevValues.lwDriverScore
          ? prevValues
          : {
              ...prevValues,
              lwDriverScore: userLW.total,
            },
      );
    };
    try {
      userState.profileID && getUserLW();
    } catch (e) {
      console.error(e.message);
    }
  }, [firebaseState, userState.profileID, lastWeek.min, lastWeek.max]);

  useEffect(() => {
    const getTeamTW = async () => {
      setStatsLoading(true);
      setTeamTW(
        await firebaseState.getCompanyGroupScores(teams, userDateRange.max),
      );
    };
    try {
      userState.profileID && teams.length && getTeamTW();
    } catch (e) {
      console.error(e.message);
    }
  }, [firebaseState, userState.profileID, userDateRange.max, teams]);

  useEffect(() => {
    const getTeamLW = async () => {
      setStatsLoading(true);
      setTeamLW(await firebaseState.getCompanyGroupScores(teams, lastWeek.max));
    };
    try {
      userState.profileID && teams.length && getTeamLW();
    } catch (e) {
      console.error(e.message);
    }
  }, [firebaseState, userState.profileID, lastWeek.max, teams]);

  useEffect(() => {
    userTW &&
      (!teams.length || teamTW.length) &&
      setAllDataTW(buildTeamArray(teamTW, userTW));
  }, [teamTW, userTW, buildTeamArray]);

  useEffect(() => {
    userLW &&
      (!teams.length || teamLW.length) &&
      setAllDataLW(buildTeamArray(teamLW, userLW));
  }, [teamLW, userLW, buildTeamArray]);

  useEffect(() => {
    try {
      const setDisplay = async () => {
        const tw = allDataTW.find(
          (team) => team.id.toString() === selectedTeam.id,
        )?.scores;
        const lw = allDataLW.find(
          (team) => team.id.toString() === selectedTeam.id,
        )?.scores;
        if (selectedTeam.id === "Personal") {
          setPersonalScore(tw);
        }
        setScoresTW(tw);
        setScoresLW(lw);
        compareTotal(tw.total, lw.total);
        setStatsLoading(false);
      };

      allDataTW.length && allDataLW.length && setDisplay();
    } catch (e) {
      setScoresTW(states.initialScores);
      setPersonalScore(states.initialScores);
      setScoresLW(states.initialScores);
      console.error(e.message);
    }
  }, [allDataLW, allDataTW, selectedTeam, compareTotal]);

  useEffect(() => {
    const getSortedScores = () => {
      let tempArr = [];
      for (var stat in scoresTW) {
        states.indivStats.includes(stat) &&
          tempArr.push([stat, scoresTW[stat]]);
      }
      tempArr.sort((a, b) => {
        return a[1] - b[1];
      });
      setSortedScores(tempArr);
    };
    const getMostImproved = () => {
      let tempArr = [];
      for (var stat in scoresTW) {
        states.indivStats.includes(stat) &&
          tempArr.push([stat, scoresTW[stat] - scoresLW[stat]]);
      }
      tempArr.sort((a, b) => {
        return a[1] - b[1];
      });
      setMostImproved(tempArr);
    };
    scoresTW && getSortedScores();
    getMostImproved();
    setStatsLoading(false);
  }, [scoresTW, scoresLW]);

  const statContext = isNaN(selectedTeam.id) ? "you" : "team";
  const bestHeaderText = t("bestStats", {
    context: statContext,
    range: rangeType,
  });
  const worstHeaderText = t("worstStats", {
    context: statContext,
    range: rangeType,
  });
  const whoText = t(statContext);

  return (
    <Box sx={contStyle}>
      <Box sx={userStatsArea}>
        <Box sx={!mobileView && { ...headerArea }}>
          <Box sx={statsTitleStyle}>
            <PageHeader
              title={t("statsAndScores")}
              description={t("statsDesc")}
            />
          </Box>
          {userLoading ? (
            <Box sx={ddlLoadBox}>
              <LoadingWheel width={3} />
            </Box>
          ) : (
            <DropdownRadioList
              options={teams}
              onUpdate={setSelectedTeam}
              showPersonalStats={true}
              profileID={userState.profileID}
            />
          )}
        </Box>
        <Typography variant="h2">{t("checkOutAverageStats")}</Typography>
        <Box sx={flippyArea}>
          {!userLoading && !userState?.profileID && (
            <Box sx={{ ...noDataBox }}>
              <Box sx={overlay} />
              <ReBox style={noDataMessage}>
                <Typography>{t("getTheAppToSeeStats")}</Typography>
              </ReBox>
            </Box>
          )}
          {mobileView ? (
            <MobileStatCards
              flipLoading={userLoading || flippyLoading}
              flippyValues={flippyValues}
            />
          ) : (
            <StatCards
              flipLoading={userLoading || flippyLoading}
              flippyValues={flippyValues}
            />
          )}
        </Box>
        <Box sx={lineBox} />
      </Box>
      <Box sx={subtitleAreaStyle}>
        <Box sx={progTextArea}>
          <Typography sx={titleStyle} variant="h2">
            {t("drivingScoreProgress")}
          </Typography>
          <Typography sx={subtitleStyle}>{t("drivingScoreDesc")}</Typography>
        </Box>
        <DateRange
          range={dateRange}
          setRange={setFormattedRange}
          updateType={setRangeType}
          style={dateRangeStyle}
          type={selectedTeam.id === "Personal"}
        />
      </Box>
      {/* Stat Area */}
      <Box sx={trackStats}>
        {/* Score Gauges */}
        <Box sx={gaugeBoxArea}>
          <Typography sx={overallHeaderStyle}>{t("overallScore")}</Typography>
          <ReBox style={gaugeBoxStyle}>
            <Box sx={!mobileView && { ...largeGaugeAreaStyle }}>
              <Box sx={largeGaugeBoxStyle} id={STATS_GAUGE}>
                {statsWheels ? (
                  <LoadingWheel width={4} />
                ) : (
                  <ScoreGauge
                    title={t("averageScore")}
                    score={scoresTW.total}
                    width={mobileView ? 150 : tabletView ? "100%" : 240}
                    type={"total"}
                  />
                )}
              </Box>
              <Typography sx={largeGaugeText}>{totalComp}</Typography>
            </Box>
            <Box
              sx={mobileView ? { ...groupGaugeMobile } : { ...groupGaugeArea }}>
              {statsWheels ? (
                <Box sx={groupGaugeBoxStyle}>
                  <LoadingWheel width={3} />
                </Box>
              ) : (
                <Box sx={groupGaugeBoxStyle}>
                  {["handling", "focus", "eco"].map((category) => (
                    <ScoreGauge
                      key={category}
                      title={t(`ScoreCategories:${category}`)}
                      score={scoresTW[category]}
                      width={mobileView ? 65 : tabletView ? "100%" : 140}
                      type={category}
                      scoresTW={scoresTW}
                      boxed={!desktopView}
                    />
                  ))}
                </Box>
              )}
              <Box sx={infoAreaStyle}>
                <Typography sx={skillScoreStyle}>{t("skillScore")}</Typography>
                <Tooltip title={t("skillScoreDesc")}>
                  <InfoIcon sx={infoIconStyle} />
                </Tooltip>
              </Box>
            </Box>
          </ReBox>
        </Box>
        {/*Stat Comparisons */}
        <Box sx={statBox}>
          <Box sx={bestRowBox}>
            <StatContainer
              width={!desktopView ? "100%" : "24%"}
              colour={blue[3]}
              title={t("bestSkill")}
              main>
              <StatDisplay
                name={sortedScores[sortedScores.length - 1][0]}
                value={sortedScores[sortedScores.length - 1][1]}
                personalValue={
                  personalScores[sortedScores[sortedScores.length - 1][0]]
                }
                compDesc={whoText}
                compValue={compareWeek(
                  sortedScores[sortedScores.length - 1][0],
                )}
                loading={statsWheels}
                colour={blue[3]}
                main
              />
            </StatContainer>
            <StatContainer
              width={!desktopView ? "100%" : "70%"}
              colour={blue[3]}
              title={bestHeaderText}>
              {mostImproved.slice(-(desktopView ? 3 : 4)).map((item, index) => (
                <>
                  <StatDisplay
                    width={desktopView ? "33%" : "50%"}
                    name={item[0]}
                    value={scoresTW[item[0]]}
                    personalValue={personalScores[item[0]]}
                    compDesc={whoText}
                    compValue={compareWeek(item[0])}
                    loading={statsWheels}
                    colour={blue[3]}
                  />
                  {desktopView && index < 2 && <Box sx={vLine} />}
                </>
              ))}
            </StatContainer>
          </Box>
          <Box sx={hLine} />
          <Box sx={worstRowBox}>
            <StatContainer
              width={!desktopView ? "100%" : "24%"}
              colour={red[2]}
              title={t("needsWork")}
              main>
              <StatDisplay
                name={sortedScores[0][0]}
                value={sortedScores[0][1]}
                personalValue={personalScores[sortedScores[0][0]]}
                compDesc={whoText}
                compValue={compareWeek(sortedScores[0][0])}
                tips={userState.isAdmin || userState.isViewer}
                loading={statsWheels}
                title={t("needsWork")}
                colour={red[2]}
                main
              />
            </StatContainer>
            <StatContainer
              width={!desktopView ? "100%" : "70%"}
              colour={red[2]}
              title={worstHeaderText}>
              {sortedScores
                .slice(0, desktopView ? 3 : 4)
                .reverse()
                .map((item, index, arr) => (
                  <>
                    <StatDisplay
                      width={desktopView ? "33%" : "50%"}
                      name={item[0]}
                      value={scoresTW[item[0]]}
                      personalValue={personalScores[item[0]]}
                      compDesc={whoText}
                      compValue={compareWeek(item[0])}
                      tips={userState.isAdmin || userState.isViewer}
                      loading={statsWheels}
                      colour={red[2]}
                    />
                    {desktopView && index < arr.length - 1 && (
                      <Box sx={vLine} />
                    )}
                  </>
                ))}
            </StatContainer>
          </Box>
        </Box>
        {!statsWheels && !scoresTW.total && (
          <Box sx={{ ...trackStats, ...noDataBox }}>
            <Box sx={overlay} />
            <ReBox style={noDataMessage}>
              <Typography>{t("getTheAppToSeeStats")}</Typography>
            </ReBox>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Stats;
